import { useState } from "react";
import { Loader2, Search, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { departureGateUnknown, formatDuration } from "@produce/shared";
import {
  applyBaggageToAll,
  applyLookupResult,
  formToLegInput,
  lookupDateFromForm,
  lookupMessage,
  passengerToForm,
  type FlightFormValues,
  type LookupFlightResult,
  type PassengerFormValues,
} from "@/lib/flightForm";

// The editor for ONE flight leg inside the flights dialog.
//
// Fully controlled: the draft lives in FlightsDialog, which is what
// makes "switch to the Return tab without pressing save" keep the
// outbound work instead of throwing it away. This component only
// renders fields and reports changes upward.
//
// The one piece of state it does own is the lookup call — that is its
// own business and nothing above needs to know about it.

export interface CrewOption {
  id: string;
  name: string;
}

interface Props {
  briefingId: string;
  legOrder: number;
  values: FlightFormValues;
  passengers: PassengerFormValues[];
  crew: CrewOption[];
  onChange: (values: FlightFormValues) => void;
  onPassengersChange: (passengers: PassengerFormValues[]) => void;
  onDelete: (() => void) | null;
  deleting: boolean;
  /**
   * Take the outbound passenger list over onto this leg. Only supplied
   * for return legs, and only when there is anything to copy.
   */
  onCopyPassengers?: () => void;
}

export function FlightLegEditor({
  briefingId,
  legOrder,
  values,
  passengers,
  crew,
  onChange,
  onPassengersChange,
  onDelete,
  deleting,
  onCopyPassengers,
}: Props) {
  const [looking, setLooking] = useState(false);
  // Drives the green pulse on the button after a successful lookup.
  // A success toast read as an error to producers ("Flight details
  // filled in" looked like a warning); confirming on the button they
  // just pressed says the same thing without alarming anyone.
  const [justFilled, setJustFilled] = useState(false);

  const selectedIds = new Set(passengers.map((p) => p.briefingCrewId));

  const set = <K extends keyof FlightFormValues>(key: K, value: FlightFormValues[K]) =>
    onChange({ ...values, [key]: value });

  const togglePassenger = (crewId: string, checked: boolean) => {
    onPassengersChange(
      checked
        ? [...passengers, passengerToForm(crewId, undefined)]
        : passengers.filter((p) => p.briefingCrewId !== crewId),
    );
  };

  const setPassengerField = (
    crewId: string,
    field: keyof Omit<PassengerFormValues, "briefingCrewId">,
    value: string,
  ) => {
    onPassengersChange(
      passengers.map((p) => (p.briefingCrewId === crewId ? { ...p, [field]: value } : p)),
    );
  };

  const handleLookup = async () => {
    const date = lookupDateFromForm(values);
    if (!values.flightNumber.trim()) {
      toast.error("Enter a flight number first.");
      return;
    }
    if (!date) {
      toast.error("Enter the departure date first.");
      return;
    }
    setLooking(true);
    setJustFilled(false);
    try {
      const { data, error } = await supabase.functions.invoke("lookup-flight", {
        body: { briefing_id: briefingId, flight_number: values.flightNumber, date },
      });
      if (error) throw error;

      const flights = (data?.flights ?? []) as LookupFlightResult[];
      if (flights.length === 0) {
        // Nothing found IS worth saying out loud — the producer has to
        // fill it in by hand and would otherwise sit waiting.
        toast.info(lookupMessage(data?.code, 0));
        return;
      }
      onChange(applyLookupResult(values, flights[0]));
      setJustFilled(true);
      window.setTimeout(() => setJustFilled(false), 2500);
      // Several matches is a decision the producer has to make, so that
      // one still gets a message.
      if (flights.length > 1) toast.info(lookupMessage(data?.code, flights.length));
    } catch {
      // The edge function never returns the upstream body, so there is
      // nothing more specific to show than "it didn't work" — and the
      // manual path is always available.
      toast.error("Flight lookup failed — fill the details in by hand.");
    } finally {
      setLooking(false);
    }
  };

  // One conversion, so both the duration preview and the "not published
  // yet" hint describe exactly what a save would store — and the hint
  // uses the same predicate the crew app does, instead of restating it.
  const asLeg = formToLegInput(values);
  const previewDuration = formatDuration(asLeg.duration_minutes);
  const gateUnknown = departureGateUnknown(asLeg);

  return (
    <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div>
          <Label htmlFor={`flight-number-${legOrder}`}>Flight number</Label>
          <Input
            id={`flight-number-${legOrder}`}
            value={values.flightNumber}
            onChange={(e) => set("flightNumber", e.target.value)}
            placeholder="KL1673"
          />
        </div>
        <div>
          <Label htmlFor={`departure-${legOrder}`}>Departure (local airport time)</Label>
          <Input
            id={`departure-${legOrder}`}
            type="datetime-local"
            value={values.departureLocal}
            onChange={(e) => set("departureLocal", e.target.value)}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleLookup}
          disabled={looking}
          className={
            justFilled
              ? "animate-pulse border-green-500 text-green-600 dark:text-green-400"
              : undefined
          }
        >
          {looking ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Search className="mr-2 h-4 w-4" />
          )}
          {justFilled ? "Found" : "Look up"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor={`from-${legOrder}`}>From (airport)</Label>
          <Input
            id={`from-${legOrder}`}
            value={values.departureAirport}
            onChange={(e) => set("departureAirport", e.target.value)}
            placeholder="AMS"
          />
        </div>
        <div>
          <Label htmlFor={`to-${legOrder}`}>To (airport)</Label>
          <Input
            id={`to-${legOrder}`}
            value={values.arrivalAirport}
            onChange={(e) => set("arrivalAirport", e.target.value)}
            placeholder="VIE"
          />
        </div>
        <div>
          <Label htmlFor={`from-name-${legOrder}`}>From (city)</Label>
          <Input
            id={`from-name-${legOrder}`}
            value={values.departureAirportName}
            onChange={(e) => set("departureAirportName", e.target.value)}
            placeholder="Amsterdam"
          />
        </div>
        <div>
          <Label htmlFor={`to-name-${legOrder}`}>To (city)</Label>
          <Input
            id={`to-name-${legOrder}`}
            value={values.arrivalAirportName}
            onChange={(e) => set("arrivalAirportName", e.target.value)}
            placeholder="Vienna"
          />
        </div>
        <div>
          <Label htmlFor={`arrival-${legOrder}`}>Arrival (local airport time)</Label>
          <Input
            id={`arrival-${legOrder}`}
            type="datetime-local"
            value={values.arrivalLocal}
            onChange={(e) => set("arrivalLocal", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor={`airline-${legOrder}`}>Airline</Label>
          <Input
            id={`airline-${legOrder}`}
            value={values.airline}
            onChange={(e) => set("airline", e.target.value)}
            placeholder="KLM"
          />
        </div>
        <div>
          <Label htmlFor={`dep-terminal-${legOrder}`}>Departure terminal</Label>
          <Input
            id={`dep-terminal-${legOrder}`}
            value={values.departureTerminal}
            onChange={(e) => set("departureTerminal", e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor={`checkin-${legOrder}`}>Check-in desk</Label>
          <Input
            id={`checkin-${legOrder}`}
            value={values.departureCheckInDesk}
            onChange={(e) => set("departureCheckInDesk", e.target.value)}
            placeholder="9-16"
          />
        </div>
        <div>
          <Label htmlFor={`arr-terminal-${legOrder}`}>Arrival terminal</Label>
          <Input
            id={`arr-terminal-${legOrder}`}
            value={values.arrivalTerminal}
            onChange={(e) => set("arrivalTerminal", e.target.value)}
          />
        </div>
      </div>

      {previewDuration && (
        <p className="text-sm text-muted-foreground">Flight time: {previewDuration}</p>
      )}

      {gateUnknown && (
        <p className="text-sm text-muted-foreground">
          The airport hasn&apos;t published a terminal or check-in desk for this flight yet. Look it
          up again closer to departure — until then the crew app tells them to check the airport
          screens.
        </p>
      )}

      <div>
        <Label htmlFor={`notes-${legOrder}`}>Notes for the crew</Label>
        <Textarea
          id={`notes-${legOrder}`}
          value={values.notes}
          onChange={(e) => set("notes", e.target.value)}
          placeholder="Be at the check-in desk two hours before departure."
          rows={2}
        />
      </div>

      <div className="rounded-md border border-border bg-background p-3">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold">Who is on this flight?</p>
          {onCopyPassengers && (
            <Button type="button" variant="outline" size="sm" onClick={onCopyPassengers}>
              <Users className="mr-2 h-4 w-4" />
              Same crew as outbound
            </Button>
          )}
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Tick everyone booked on it. Each of them sees only their own details in the app.
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {crew.map((member) => (
            <label key={member.id} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={selectedIds.has(member.id)}
                onCheckedChange={(checked) => togglePassenger(member.id, checked === true)}
              />
              <span className="truncate">{member.name}</span>
            </label>
          ))}
          {crew.length === 0 && (
            <p className="col-span-full text-sm text-muted-foreground">
              Add crew members in the crew step first.
            </p>
          )}
        </div>

        {passengers.length > 0 && (
          <div className="mt-4 space-y-3">
            {passengers.map((p) => (
              <div key={p.briefingCrewId} className="rounded-md border border-border p-3">
                <p className="mb-2 text-sm font-medium">
                  {crew.find((c) => c.id === p.briefingCrewId)?.name ?? "Crew member"}
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Input
                    value={p.bookingReference}
                    onChange={(e) =>
                      setPassengerField(p.briefingCrewId, "bookingReference", e.target.value)
                    }
                    placeholder="Booking reference"
                  />
                  <Input
                    value={p.seat}
                    onChange={(e) => setPassengerField(p.briefingCrewId, "seat", e.target.value)}
                    placeholder="Seat"
                  />
                  <Input
                    value={p.baggage}
                    onChange={(e) => setPassengerField(p.briefingCrewId, "baggage", e.target.value)}
                    placeholder="Baggage, e.g. 1x 23kg checked + 1x cabin"
                  />
                  <Input
                    value={p.specialBaggage}
                    onChange={(e) =>
                      setPassengerField(p.briefingCrewId, "specialBaggage", e.target.value)
                    }
                    placeholder="Equipment, e.g. 1 flightcase (max 32kg)"
                  />
                </div>
              </div>
            ))}
            {passengers.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onPassengersChange(applyBaggageToAll(passengers))}
              >
                Use the first baggage allowance for everyone
              </Button>
            )}
          </div>
        )}
      </div>

      {onDelete && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive"
          onClick={onDelete}
          disabled={deleting}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Remove this flight
        </Button>
      )}
    </div>
  );
}
